"use client";

import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type { Control, FieldValues, Path } from "react-hook-form";
import { ReactNode } from "react";
import { cn } from "@/lib/utils"; // ensure you have this for className merging
import { ScrollArea } from "@/components/ui/scroll-area";
import { I18nNamespace } from "@/i18n/generated/keys.generated";
interface OptionType {
  label: string;
  value: string;
  disabled?: boolean;
}

interface CustomSelectProps<T extends FieldValues = FieldValues> {
  name: Path<T>;
  control: Control<T>;
  options: OptionType[];
  placeholder?: string;
  placeholderClassName?: string;
  width?: string;
  label?: ReactNode;
  classNameLabel?: string;
  disabled?: boolean;
  className?: string;
  onValueChange?: (value: string) => void;
  uniqueId?: string;
  keyT: I18nNamespace;
}

export default function SelectForm<T extends FieldValues>({
  name,
  control,
  options,
  placeholder,
  placeholderClassName,
  width = "w-full",
  label,
  classNameLabel,
  disabled,
  className,
  uniqueId,
  keyT,
}: CustomSelectProps<T>) {
  return (
    <FormField
      name={name}
      control={control}
      render={({ field }) => (
        <FormItem className={width}>
          {label && (
            <FormLabel className={cn(classNameLabel)}>{label}</FormLabel>
          )}
          <FormControl>
            <Select
              onValueChange={field.onChange}
              value={field.value}
              disabled={disabled}
            >
              <SelectTrigger
                disabled={disabled}
                className={cn(
                  "!h-13 border-1 border-gray-500 shadow rounded-md font-medium p-3",
                  width,
                  className,
                  disabled && "bg-muted opacity-50 cursor-not-allowed",
                )}
              >
                <SelectValue
                  placeholder={placeholder}
                  placeholderClassName={placeholderClassName}
                />
              </SelectTrigger>
              <SelectContent>
                <ScrollArea className="max-h-55 overflow-y-auto">
                  {options.map((option, optionIndex) => (
                    <SelectItem
                      key={`${uniqueId || name}-${option.value}-${optionIndex}`}
                      value={option.value}
                    >
                      {option.label}
                    </SelectItem>
                  ))}
                </ScrollArea>
              </SelectContent>
            </Select>
          </FormControl>
          <FormMessage keyT={keyT} />
        </FormItem>
      )}
    />
  );
}
